import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import BitbucketBranchesIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import BitbucketReposIcon from '@atlaskit/icon/glyph/bitbucket/repos';
import Select, { OptionType, FormatOptionLabelMeta } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';

import {
  getBranchViewDestinationRepoName,
  getBranchCanViewParentRepo,
  getBranchViewDestinationBranchName,
} from 'src/redux/repository/branch/branch-redux';
import { BranchSelector } from 'src/sections/repository/components/ref-selector/branch-selector';
import * as styles from 'src/sections/repository/components/ref-selector/branch-selector.styled';
import { getRepositoryDetails } from 'src/selectors/repository-selectors';

import { useRefSelectorActions } from '../hooks/use-ref-selector';

import { messages } from './branch-page.i18n';

export const CompareWidget = ({
  branchName,
  repositoryFullSlug,
}: {
  branchName: string;
  repositoryFullSlug: string;
}): JSX.Element => {
  // having a parent means the repo is forked
  // must use this selector to ensure we have read access to the parent
  const showParent = useSelector(getBranchCanViewParentRepo);
  const { parent } = useSelector(getRepositoryDetails);

  const destRepoName = useSelector(getBranchViewDestinationRepoName);
  const destBranchName = useSelector(getBranchViewDestinationBranchName) || '';

  const { state, fetchRefOptions, onChangeToBranch, onChangeToRepository } =
    useRefSelectorActions();

  const formatOptionLabel = (
    option: OptionType,
    { context }: FormatOptionLabelMeta<OptionType>
  ) => {
    if (context === 'value') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <BitbucketReposIcon size="medium" label="" />
          <span
            style={{
              paddingLeft: 8,
              paddingBottom: 0,
            }}
          >
            {option.label}
          </span>
        </div>
      );
    }
    return option.label;
  };

  const renderFromLabels = () => {
    return (
      <>
        <styles.BranchLabels>
          {showParent && (
            <Tooltip content={repositoryFullSlug} position="bottom">
              <styles.BranchLabel>
                <BitbucketReposIcon label="" />
                <span>{repositoryFullSlug}</span>
              </styles.BranchLabel>
            </Tooltip>
          )}

          <Tooltip content={branchName} position="bottom">
            <styles.BranchLabel>
              <BitbucketBranchesIcon label="" />
              <span>{branchName}</span>
            </styles.BranchLabel>
          </Tooltip>
        </styles.BranchLabels>
      </>
    );
  };

  const renderToLabels = () => {
    return (
      <>
        <styles.BranchLabels>
          {showParent && parent && destRepoName && (
            <styles.FieldRefSelector>
              <Select
                options={[
                  { label: repositoryFullSlug, value: repositoryFullSlug },
                  { label: parent.full_name, value: parent.full_name },
                ]}
                value={{ label: destRepoName, value: destRepoName }}
                onChange={onChangeToRepository}
                formatOptionLabel={formatOptionLabel}
              />
            </styles.FieldRefSelector>
          )}

          <styles.FieldRefSelector>
            <BranchSelector
              branchSelector={state}
              value={{ label: destBranchName, value: destBranchName }}
              onChangeBranch={onChangeToBranch}
              fetchRefOptions={fetchRefOptions}
            />
          </styles.FieldRefSelector>
        </styles.BranchLabels>
      </>
    );
  };

  return (
    <>
      <styles.CompareHeader>
        <FormattedMessage tagName="h2" {...messages.compare} />
      </styles.CompareHeader>

      <styles.CompareBranchFilter>
        {renderFromLabels()}
        <styles.Arrow>
          <ArrowRightIcon label="" />
        </styles.Arrow>
        {renderToLabels()}
      </styles.CompareBranchFilter>
    </>
  );
};
